import { Button, Dialog, SelectionCardGroup, Stack, StackItem, Typography } from '@optimus-web/core';
import { lt, useFeatureFlag, useFormControls } from '@mews/framework';
import { OptimusProvider, SuspenseBoundary } from '@mews-commander/ui';
import { FeatureFlag } from '@mews-commander/enums';
import { PropertyGroupSelectField, PropertySelectField } from '../../../../components/enterprise';
import { AssignPropertiesFieldNames, AssignPropertiesSegment } from '../../../enterprisePolicies/_shared/types';
import {
    assignPropertiesDefaultValues,
    AssignPropertiesFormValues,
    assignPropertiesResolver,
} from '../../../enterprisePolicies/_shared/schemas';
import { useEnterprisesProductContext } from '../../context/EnterprisesProductContext';

interface Props {
    onCancel: () => void;
    onConfirm: () => void;
    propertyIds?: string[];
}

export const AddProductToPropertyDialog = ({ onConfirm, onCancel, propertyIds }: Props) => {
    const { setState } = useEnterprisesProductContext();

    const handleSubmit = (values: AssignPropertiesFormValues) => {
        const mode = values.segment;

        if (mode === AssignPropertiesSegment.Properties) {
            setState({ selectedEnterpriseIds: values.properties });
        } else {
            setState({ selectedEnterpriseGroupIds: values.propertyGroups });
        }

        onConfirm();
    };

    const propertyGroupEnabled = useFeatureFlag(FeatureFlag.centralProductsConfigurePropertyGroupsEnabled);

    const { setValue, submit, watch, renderFormContent } = useFormControls<AssignPropertiesFormValues>({
        defaultValues: propertyGroupEnabled
            ? assignPropertiesDefaultValues
            : {
                  [AssignPropertiesFieldNames.Segment]: AssignPropertiesSegment.Properties,
                  [AssignPropertiesFieldNames.Properties]: [],
              },
        resolver: assignPropertiesResolver,
        onSubmit: handleSubmit,
    });

    const selectedControlValue = watch(AssignPropertiesFieldNames.Segment);
    const selectedProperties = watch(AssignPropertiesFieldNames.Properties);
    const selectedPropertyGroups = watch(AssignPropertiesFieldNames.PropertyGroups);

    const isAddDisabled =
        (!selectedProperties || selectedProperties.length === 0) &&
        (!selectedPropertyGroups || selectedPropertyGroups.length === 0);

    return (
        <Dialog
            title={lt('AddProductToPropertyDialogTitle')}
            footer={{
                primary: (
                    <Button onClick={submit} variant="primary" size="medium" type="submit" disabled={isAddDisabled}>
                        {lt('Add')}
                    </Button>
                ),
                secondary: (
                    <Button onClick={onCancel} size="medium" type="submit">
                        {lt('DoItLater')}
                    </Button>
                ),
            }}
            active
            onClose={onCancel}
        >
            <OptimusProvider density="spacious">
                {renderFormContent(
                    <Stack vertical spacing="300">
                        {propertyGroupEnabled && (
                            <>
                                <Typography textStyle="bodyMedium" as="div">
                                    {lt('AddProductToPropertyDialogDescription')}
                                </Typography>
                                <SelectionCardGroup
                                    templateColumns="repeat(2, 1fr)"
                                    type="radio"
                                    onChange={(e, value) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setValue(AssignPropertiesFieldNames.Segment, value as AssignPropertiesSegment);
                                    }}
                                    inputValue={selectedControlValue}
                                    options={[
                                        ...(propertyGroupEnabled
                                            ? [
                                                  {
                                                      label: lt('PropertyGroupPlural'),
                                                      name: AssignPropertiesSegment.PropertyGroups,
                                                      value: AssignPropertiesSegment.PropertyGroups,
                                                  },
                                              ]
                                            : []),
                                        {
                                            label: lt('Properties'),
                                            name: AssignPropertiesSegment.Properties,
                                            value: AssignPropertiesSegment.Properties,
                                        },
                                    ]}
                                />
                            </>
                        )}
                        <SuspenseBoundary>
                            <StackItem>
                                {selectedControlValue === AssignPropertiesSegment.PropertyGroups &&
                                propertyGroupEnabled ? (
                                    <PropertyGroupSelectField required shouldUnregister />
                                ) : (
                                    <PropertySelectField
                                        compact
                                        required
                                        multiselect
                                        shouldUnregister
                                        propertyIds={propertyIds}
                                    />
                                )}
                            </StackItem>
                        </SuspenseBoundary>
                    </Stack>
                )}
            </OptimusProvider>
        </Dialog>
    );
};
